import type { Theme } from '@mui/material/styles';
import type { SxStyles } from '@/ui/style/types';
import {
  NAV_BAR_OVERVIEW_OFFSET,
  NAV_BAR_BOTTOM_OFFSET,
  NAV_BAR_BOTTOM_OFFSET_EXPANDED,
  NAV_BAR_BUTTON_GROUP_MAX_HEIGHT,
  OVERVIEW_MAP_MIN_CONTAINER_WIDTH,
  OVERVIEW_MAP_MIN_CONTAINER_HEIGHT,
} from '@/core/utils/constant';

/**
 * Gets custom sx classes for the navigation bar.
 *
 * @param theme - The theme object
 * @param panelWidth - Optional panel width (px or string). If not provided, defaults to auto-sizing.
 * @returns The sx classes object
 */
export const getSxClasses = (theme: Theme, panelWidth?: string | number): SxStyles => {
  /** Container query string for minimum size thresholds (used by multi-column layout styles). */
  const mapContainerMinSizeQuery = `@container map (min-width: ${OVERVIEW_MAP_MIN_CONTAINER_WIDTH}px) and (min-height: ${OVERVIEW_MAP_MIN_CONTAINER_HEIGHT}px)`;

  return {
    navBarContainer: {
      position: 'absolute',
      right: '8px',
      top: '8px',
      bottom: NAV_BAR_BOTTOM_OFFSET,
      left: 'auto',
      width: 'auto',
      maxWidth: '90vw',
      minHeight: '100px',
      zIndex: 150,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'safe flex-end',
      gap: theme.spacing(1),
      alignItems: 'center',
      overflowY: 'auto',
      padding: theme.spacing(0.25),
      backgroundColor: 'transparent',
      borderRadius: theme.shape.borderRadiusMd,
      pointerEvents: 'all',
      scrollbarWidth: 'thin',
    },
    // Add top offset to nav bar when overview map is visible to avoid overlap
    navBarContainerWithOverview: {
      top: NAV_BAR_OVERVIEW_OFFSET,
    },
    /**
     * Enables multi-column flex layout when the map container meets minimum size requirements.
     *
     * Container queries trigger at the same thresholds used for overview map visibility
     * (OVERVIEW_MAP_MIN_CONTAINER_WIDTH and OVERVIEW_MAP_MIN_CONTAINER_HEIGHT).
     *
     * This ensures the nav-bar uses available space optimally regardless of whether
     * the overview map is visible.
     */
    navBarContainerMultiColumn: {
      [mapContainerMinSizeQuery]: {
        flexFlow: 'column wrap-reverse',
        overflowY: 'hidden',
      },
    },
    // Add bottom offset to nav bar when map-info bar is expanded to avoid overlap
    navBarContainerWithExpandedMapInfo: {
      bottom: NAV_BAR_BOTTOM_OFFSET_EXPANDED,
    },
    navBtnGroupColumns: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(1),
      alignItems: 'center',
    },
    navBtnGroup: {
      backgroundColor: theme.palette.background.paper,
      overflow: 'clip',
      '& .MuiButtonGroup-grouped:not(:last-child)': {
        borderColor: theme.palette.divider,
      },
    },
    /**
     * Enables button wrapping within groups when container meets minimum size.
     *
     * See navBarContainerMultiColumn for threshold rationale.
     */
    navBtnGroupMultiColumn: {
      [mapContainerMinSizeQuery]: {
        flexWrap: 'wrap',
        maxHeight: NAV_BAR_BUTTON_GROUP_MAX_HEIGHT,
      },
    },
    navButton: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      borderRadius: theme.shape.borderRadiusNone,
      width: '44px',
      height: '44px',
      maxWidth: '44px',
      minWidth: '44px',
      padding: 'initial',
      transition: theme.transitions.create('background-color', {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
      '&.highlighted.active': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '& .MuiSvgIcon-root': {
          color: `inherit`,
          fill: theme.palette.primary.contrastText,
          stroke: theme.palette.primary.contrastText,
        },
      },
      // Override global focus indicator for NavBar buttons
      '&.Mui-focusVisible': {
        outlineColor: 'transparent',
        boxShadow: 'none',
      },
    },
    popper: {
      zIndex: theme.zIndex.modal,
    },
    popoverPaper: {
      // If panelWidth is provided, use it; otherwise auto-size with constraints
      width: panelWidth ?? 'auto',
      minWidth: '180px',
      maxWidth: '70vw',
      maxHeight: 'min(100vh, 500px)',
      display: 'flex',
      flexDirection: 'column',
      marginRight: theme.spacing(1),
    },
    popoverTitleContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      minHeight: '48px',
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0.5, 1, 0.5, 2),
      flexShrink: 0,
      gap: theme.spacing(1),
    },
    popoverTitleLabel: {
      color: theme.palette.text.primary,
      flexShrink: 1,
      padding: theme.spacing(0),
    },
    popoverTitleActions: {
      display: 'flex',
      flexShrink: 0,
      color: theme.palette.text.primary,
    },
    popoverContent: {
      '&.MuiDialogContent-root': {
        padding: theme.spacing(2, 2),
        flexGrow: 1 /* Forces this child to fill all remaining space */,
        minHeight: 0,
        overflowY: 'auto',
        scrollbarWidth: 'thin',
      },
    },
    listItem: {
      marginBottom: theme.spacing(0.75), // Create space for the focus indicator to be visible
    },
    button: {
      justifyContent: 'flex-start',
      '&[aria-pressed="true"]': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        // Force icon colors to white when pressed
        '& .MuiSvgIcon-root': {
          color: theme.palette.primary.contrastText,
          fill: theme.palette.primary.contrastText,
          stroke: theme.palette.primary.contrastText,
        },
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        },
      },
    },
    rotationControlContainer: {
      width: '100%',
      maxWidth: '300px',
      padding: theme.spacing(0, 2),
    },
    rotationLabelBox: {
      display: 'flex',
      justifyContent: 'center',
      paddingTop: theme.spacing(1.25),
      minWidth: 0,
    },
    rotationLabel: {
      fontWeight: theme.typography.fontWeightBold,
    },
    rotationLabelProjection: {
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'inline',
      },
    },
    rotationButtonContainer: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      alignItems: 'center',
      marginTop: theme.spacing(1.25),
      gap: theme.spacing(0.5),
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
      },
    },
    rotationIconBase: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
    },
    measurementPanel: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    measurementToggleGroup: {
      '& .MuiToggleButton-root': {
        gap: theme.spacing(1), // Adds spacing between icon and text
      },
      '& .MuiToggleButton-root.Mui-selected': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        },
      },
    },
    measurementClearButton: {
      alignSelf: 'center',
    },
  };
};
